import React from 'react'
import * as H from 'history'
import Button from 'react-bootstrap/Button'
import { DocumentReformController } from '../../../controllers/DocumentReformController'
import { ISupportSpinner } from '../../../shared/Ajax'
import { ApplicationController } from '../../../controllers/ApplicationController'
import { ReportingRequirementSummaryModel } from '../../../models/ReportingRequirementSummaryModel'
import { TableBody, TableColumn } from '../../../shared/table'
import { ReportingRequirementModel } from '../../../models/ReportingRequirementModel'
import { MilestoneReportController } from '../../../controllers/MilestoneReportController'
import { DashboardModel } from '../../../models/DashboardModel'
import { formatNumber } from '../../../shared/Utility'

interface Props {
    reportingRequirementSummary: ReportingRequirementSummaryModel
    history: H.History
    claimRandomIdentifier: string
    model: DashboardModel
}

export interface State {
    loading: boolean
    issueHistory: any
}

export default class ReportingRequirement extends React.Component<Props, State> implements ISupportSpinner {
    docAjax: DocumentReformController
    ajax: ApplicationController
    ajaxMilestone: MilestoneReportController

    constructor(props: Props) {
      super(props)
      this.docAjax = new DocumentReformController()
      this.ajax = new ApplicationController()
      this.ajaxMilestone = new MilestoneReportController()

      this.state = {
        loading: false,
        issueHistory: null,
      }
    }

    toggleSpinner(on: boolean, callback: { (): any }): void {
      this.setState({ loading: on }, callback)
    }

    createMilestone(reportingRequirementId: string, reportingPeriodStartDate: Date, reportingPeriodEndDate: Date) {
      const { model, history } = this.props
      const applicationDetails = model?.dashboardDetailModels?.[0]
      const { grantAgreementID, applicationID } = applicationDetails.grantAgreementModel

      this.ajaxMilestone.create(this, applicationID, grantAgreementID, reportingRequirementId, reportingPeriodStartDate, reportingPeriodEndDate, (milestoneID: string) => {
        console.log(milestoneID)
        history.push(`/milestone-report/${milestoneID}/generalRequirement`)
      })
    }

    handleResume = (milestoneReportId: string, canResumeMilestoneReport: boolean) => {
      if (!canResumeMilestoneReport) {
        alert('You cannot resume this milestone report as it\'s before the Start Date, or the Due Date has passed.')

        return
      }
      const { history } = this.props
      history.push(`/milestone-report/${milestoneReportId}/generalRequirement`)
    }

    handleDelete = (milestoneReportID: string) => {
      if (!confirm('Are you sure you want to delete this Milestone Report.')) {
        return
      }
      const { model, history } = this.props

      this.ajaxMilestone.delete(this, milestoneReportID, () => {
        history.replace('/') // previously /dashboard-reform
      })
    }

    openFile(applicationID: string, milestoneReportBlobID: string) {
      fetch(`/api/DocumentReform/downloadMilestoneReport?applicationId=${applicationID}&blobid=${milestoneReportBlobID}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/pdf' },
      })
        .then((res) => res.blob())
        .then((response) => {
          const file = new Blob([response], {
            type: 'application/pdf',
          })
          const fileURL = URL.createObjectURL(file)
          window.open(fileURL)
        })
        .catch((error) => {
          console.log(error)
        })
    }

    render() {
      const {
        loading, issueHistory,
      } = this.state

      const {
        history, reportingRequirementSummary, reportingRequirementSummary: {
          grantAgreementNumber, grantType, round, totalGrantAmount,
        },
      } = this.props

      let columns: TableColumn<ReportingRequirementModel>[]

      columns = [
        { name: 'Reporting Period', content: (r) => r.reportingPeriod },
        { name: 'Report Type', content: (r) => r.reportType },
        { name: 'Maximum amount of payment for this Reporting Period', width: '15%', content: (r) => (r.maximumPaymentAmount !== '' ? `$${formatNumber(+r.maximumPaymentAmount)}` : 'NA') },
        { name: 'Due date', content: (r) => r.dueDate, responsive: 'md' },
        { name: 'Status', content: (r) => r.milestoneReportStatus },
        // { name: 'Grant Paid', content: (r) => (r.grantPaid !== null ? "$" + formatNumber(+r.grantPaid) : ""), responsive: 'md' },
        {
          name: '',
          content: (r) => (r.milestoneReportId == null && r.canStartMilestoneReport === true && r.isAlreadyStarted === false
            ? (
              <Button className="emdg-btn ml-2" id="btnStart" onClick={() => this.createMilestone(r.reportingRequirementId, r.reportingPeriodStartDate, r.reportingPeriodEndDate)}>
                Start
              </Button>
            )
            : (r.milestoneReportStatus === 'Draft' && r.isAlreadyStarted === true
              ? (
                <div className="row">
                  <div className="col-md-6 text-left">
                    <Button className='emdg-btn emdg-btn' id="btnDelete" onClick={() => this.handleDelete(r.milestoneReportId)}>
                      Delete
                    </Button>
                  </div>
                  <div className="col-md-6 text-right">
                    <Button className="emdg-btn ml-2" id="btnResume" onClick={() => this.handleResume(r.milestoneReportId, r.canStartMilestoneReport)}>Resume</Button>
                  </div>
                </div>
              )
              : (r.milestoneReportId != null && r.milestoneReportStatus !== 'Draft'
                ? (
                  <Button className="emdg-btn ml-2" id="btnViewMilestoneReport" onClick={() => this.openFile(r.applicationId, r.milestoneReportBlobID)}>
                    View
                  </Button>
                ) : ''))),
        },
      ]

      return (
        <>
          <h3>Reports</h3>
          <div className="emdg-callout mt-3 mb-4">
            <div className="row">
              <div className="col-lg-3 col-md-12 d-flex mb-2">
                <strong>Grant Agreement  Number</strong>
              </div>
              <div className="col-lg-3 col-md-12 d-flex mb-2">
                {grantAgreementNumber}
              </div>
              <div className="col-lg-3 col-md-12 d-flex">
                <strong>Type of Grant</strong>
              </div>
              <div className="col-lg-3 col-md-12 d-flex">
                {grantType}
              </div>
            </div>
            <div className="row">
              <div className="col-lg-3 col-md-12 d-flex">
                <strong>Round</strong>
              </div>
              <div className="col-lg-3 col-md-12 d-flex mb-2">
                {round}
              </div>
              <div className="col-lg-3 col-md-12 d-flex">
                <strong>Total Grant Amount</strong>
              </div>
              <div className="col-lg-3 col-md-12 d-flex mb-2">
                {totalGrantAmount !== null ? `$${formatNumber(+totalGrantAmount)}` : ''}
              </div>
            </div>
          </div>

          <TableBody<ReportingRequirementModel>
            id="reportingRequirements"
            columns={columns}
            value={reportingRequirementSummary.reportingRequirementList}
            alwaysShowAllRows
            noRowsMessage="No search results returned"
          />
        </>
      )
    }
}
